import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Tabs, TabList, TabPanels, Tab, TabPanel, useColorModeValue } from '@chakra-ui/react';
import {
	Container,
	Box,
	Flex,
	Text,
	Center,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	useDisclosure,
	Stack,
	Image,
	ListItem,
	SimpleGrid,
	useBreakpointValue,
	Button,
	Badge,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SmallDownloadAndViewButton from '../../components/Files/SmallDownloadAndViewButton';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import moment from 'moment/moment';
import c from '../../constant';
import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import QrScannerWithFlip from '../../utils/QrScannerWithFlip';

const LOAD_AIRM_DETAIL = gql`
	query getAllAIRMRegistrations(
		$email: String
		$title: String
		$firstName: String
		$lastName: String
		$nameOfOrganisation: String
		$country: String
		$formType: String
		$nameOfAssociation: String
		$association: String
		$countryAssociation: String
	) {
		getAllAIRMRegistrations(
			email: $email
			title: $title
			firstName: $firstName
			lastName: $lastName
			nameOfOrganisation: $nameOfOrganisation
			country: $country
			formType: $formType
			nameOfAssociation: $nameOfAssociation
			association: $association
			countryAssociation: $countryAssociation
		) {
			airmForm {
				id
				createdAt
				email
				title
				firstName
				lastName
				nameOfOrganisation
				designation
				country
				statusOfParticipation
				phoneNumber
				idPhoto
				passportAttachment
				event
				dinnerEvent
				dietary
				flightNo
				airline
				airport
				dateTimeOfArrival
				dateTimeOfDeparture
				hotel
				checkIn
				checkOut
				nameOfAccompanyPerson
				relationship
				checkInStatus
				checkInAt
				checkOutStatus
				checkOutAt
				formType
				nameOfAssociation
				association
				countryAssociation
				checkInAssociation
				checkOutAssociation
			}
			totalPages
			totalRows
		}
	}
`;

const LOAD_AIRM_BY_ID = gql`
	query GetAIRMRegistrationById($id: String!) {
		getAIRMRegistrationById(id: $id) {
			id
			createdAt
			email
			title
			firstName
			lastName
			nameOfOrganisation
			designation
			country
			statusOfParticipation
			phoneNumber
			idPhoto
			passportAttachment
			event
			dinnerEvent
			dietary
			flightNo
			airline
			airport
			dateTimeOfArrival
			dateTimeOfDeparture
			hotel
			checkIn
			checkOut
			nameOfAccompanyPerson
			relationship
			checkInStatus
			checkInAt
			checkOutStatus
			checkOutAt
		}
	}
`;

const CHECK_IN_GUEST = gql`
	mutation GetCheckInGuest($id: String!) {
		checkInGuest(id: $id) {
			id
			checkInStatus
			checkInAt
		}
	}
`;

const CHECK_OUT_GUEST = gql`
	mutation GetCheckOutGuest($id: String!) {
		checkOutGuest(id: $id) {
			id
			checkOutStatus
			checkOutAt
		}
	}
`;

const CHECK_OUT_ASSOCIATION = gql`
	mutation CheckOutAssociation($id: String!) {
		checkOutAssociation(id: $id) {
			id
			checkOutAssociation
		}
	}
`;

const EXPORT_AIRM_ALL = gql`
	query ExportAllAIRMRegistrations {
		getAllAIRMRegistrations {
			airmForm {
				id
				createdAt
				email
				title
				firstName
				lastName
				nameOfOrganisation
				designation
				country
				statusOfParticipation
				phoneNumber
				event
				dinnerEvent
				dietary
				flightNo
				airline
				airport
				dateTimeOfArrival
				dateTimeOfDeparture
				hotel
				checkIn
				checkOut
				nameOfAccompanyPerson
				relationship
				checkInAt
				checkInStatus
				checkOutAt
				formType
			}
		}
	}
`;

const EXPORT_ASSOCIATION_ALL = gql`
	query ExportAllAssociationRegistrations {
		getAllAssociationRegistrations {
			airmForm {
				formType
				nameOfAssociation
				association
				checkInAssociation
			}
		}
	}
`;

function AIRMReport() {
	const location = useLocation();
	const { t } = useTranslation();
	const urlParams = new URLSearchParams(location.search);
	const email = urlParams.get('email') ? urlParams.get('email') : null;
	const title = urlParams.get('title') ? urlParams.get('title') : null;
	const firstName = urlParams.get('firstName') ? urlParams.get('firstName') : null;
	const lastName = urlParams.get('lastName') ? urlParams.get('lastName') : null;
	const nameOfOrganisation = urlParams.get('nameOfOrganisation') ? urlParams.get('nameOfOrganisation') : null;
	const country = urlParams.get('country') ? urlParams.get('country') : null;
	const formType = urlParams.get('formType') ? urlParams.get('formType') : null;
	const nameOfAssociation = urlParams.get('nameOfAssociation') ? urlParams.get('nameOfAssociation') : null;
	const association = urlParams.get('association') ? urlParams.get('association') : null;
	const countryAssociation = urlParams.get('countryAssociation') ? urlParams.get('countryAssociation') : null;
	const [rowLimit] = useState(10);
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isOpenQr, onOpen: onOpenQr, onClose: onCloseQr } = useDisclosure();
	const { isOpen: isOpenConfirm, onOpen: onOpenConfirm, onClose: onCloseConfirm } = useDisclosure();
	const [scannedGuestId, setScannedGuestId] = useState(null);
	const [qrResult, setQrResult] = useState(null);
	const [selectedGuest, setSelectedGuest] = useState(null);
	const [sortCheckInAsc, setSortCheckInAsc] = useState(null);
	const [sortCountryAsc, setSortCountryAsc] = useState(null);
	const [detail, setDetail] = useState(null);
	const [tabIndex, setTabIndex] = useState(0);

	const [loadDetail, { data, error, loading }] = useLazyQuery(LOAD_AIRM_DETAIL);
	const [loadDetailById, { data: dataDetailById, error: errorDetailById, loading: loadingDetailById }] = useLazyQuery(LOAD_AIRM_BY_ID);
	const [loadCheckInGuest, { data: dataCheckInGuest, loading: loadingCheckInGuest }] = useMutation(CHECK_IN_GUEST);
	const [loadCheckOutGuest, { data: dataCheckOutGuest, loading: loadingCheckOutGuest }] = useMutation(CHECK_OUT_GUEST);
	const [loadCheckOutAssociation, { data: dataCheckOutAssociation, loading: loadingCheckOutAssociation }] = useMutation(CHECK_OUT_ASSOCIATION);
	const [loadExportData] = useLazyQuery(EXPORT_AIRM_ALL, {
		fetchPolicy: 'network-only',
		onCompleted: (res) => {
			const rows = res?.getAllAIRMRegistrations?.airmForm ?? [];
			const guests = rows.filter((r) => r.formType !== 'association');
			if (!guests.length) return;

			const headers = [
				{ label: 'No', key: 'no' },
				{ label: 'Registered On', key: 'createdAt' },
				{ label: 'Email', key: 'email' },
				{ label: 'Title', key: 'title' },
				{ label: 'First Name', key: 'firstName' },
				{ label: 'Last Name', key: 'lastName' },
				{ label: 'Name of Organisation', key: 'nameOfOrganisation' },
				{ label: 'Designation', key: 'designation' },
				{ label: 'Country', key: 'country' },
				{ label: 'Status of Participation', key: 'statusOfParticipation' },
				{ label: 'Phone Number / WhatsApp', key: 'phoneNumber' },
				{ label: 'Event Attendance', key: 'event' },
				{ label: 'Dinner Attendance', key: 'dinnerEvent' },
				{ label: 'Dietary Requirements', key: 'dietary' },
				{ label: 'Flight No.', key: 'flightNo' },
				{ label: 'Airline', key: 'airline' },
				{ label: 'Airport', key: 'airport' },
				{ label: 'Date/Time of Arrival', key: 'dateTimeOfArrival' },
				{ label: 'Date/Time of Departure', key: 'dateTimeOfDeparture' },
				{ label: 'Hotel', key: 'hotel' },
				{ label: 'Check-in Date', key: 'checkIn' },
				{ label: 'Check-out Date', key: 'checkOut' },
				{ label: 'Name of Accompanying Person', key: 'nameOfAccompanyPerson' },
				{ label: 'Relationship to Accompanying Person', key: 'relationship' },
				{ label: 'Check-in Time', key: 'checkInAt' },
				{ label: 'Check-out Time', key: 'checkOutAt' },
			];

			const csvRows = guests?.map((row, i) =>
				headers
					.map(({ key }) => {
						if (key === 'no') {
							return i + 1 < 10 ? `0${i + 1}` : i + 1;
						}

						const val = row[key];
						if (['dateTimeOfArrival', 'dateTimeOfDeparture', 'createdAt', 'checkIn', 'checkOut', 'checkInAt', 'checkOutAt'].includes(key)) {
							return `"${timeFormat(val)}"`;
						}
						if (Array.isArray(val)) return `"${val.join(', ')}"`;
						if (val instanceof Date) return val.toISOString();
						if (val === null || val === undefined) return '';
						if (typeof val === 'boolean') return val ? 'Yes' : 'No';
						return `"${String(val).replace(/"/g, '""')}"`;
					})
					.join(',')
			);
			const csvHeader = headers.map((h) => h.label).join(',');
			// const csvContent = [csvHeader, ...csvRows].join('\n');
			// const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

			const BOM = '\uFEFF'; // UTF-8 BOM
			const csvContent = [csvHeader, ...csvRows].join('\n');
			const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `28th_AIRM_${new Date().toISOString().slice(0, 10)}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		onError: (err) => {
			console.error('CSV export failed:', err);
		},
	});

	const [loadAssociationExportData] = useLazyQuery(EXPORT_ASSOCIATION_ALL, {
		fetchPolicy: 'network-only',
		onCompleted: (res) => {
			const association = all.filter((option) => option.formType === 'association');

			if (!association.length) return;

			const headers = [
				{ label: 'No', key: 'no' },
				{ label: 'Full name', key: 'nameOfAssociation' },
				{ label: 'Name of Institution', key: 'association' },
				{ label: 'Check-in', key: 'checkInAssociation' },
				{ label: 'Check-out', key: 'checkOutAssociation' },
			];

			const csvRows = association?.map((row, i) =>
				headers
					.map(({ key }) => {
						if (key === 'no') {
							return i + 1 < 10 ? `0${i + 1}` : i + 1;
						}

						const val = row[key];
						if (['checkInAssociation', 'checkOutAssociation'].includes(key)) {
							return `"${timeFormat(val)}"`;
						}
						if (Array.isArray(val)) return `"${val.join(', ')}"`;
						if (val instanceof Date) return val.toISOString();
						if (val === null || val === undefined) return '';
						if (typeof val === 'boolean') return val ? 'Yes' : 'No';
						return `"${String(val).replace(/"/g, '""')}"`;
					})
					.join(',')
			);
			const csvHeader = headers.map((h) => h.label).join(',');
			const csvContent = [csvHeader, ...csvRows].join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `28th AIRM & 51st_AIC_${new Date().toISOString().slice(0, 10)}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		onError: (err) => {
			console.error('CSV export failed:', err);
		},
	});

	useEffect(() => {
		if (dataDetailById?.getAIRMRegistrationById) {
			setDetail(dataDetailById?.getAIRMRegistrationById);
		}
	}, [dataDetailById?.getAIRMRegistrationById]);

	const showLargeScreen = useBreakpointValue({
		base: false,
		sm: false,
		md: true,
		lg: true, // >= 992px
		xl: true, // >= 1280px
	});

	const timeFormat = (time) => (time ? moment(time).format('D MMM YYYY, h:mm a') : '-');

	useEffect(() => {
		window.scrollTo(0, 0);
		loadDetail({
			variables: {
				limit: rowLimit,
				offset: currentPage ? (currentPage - 1) * rowLimit : 0,
				email,
				title,
				firstName,
				lastName,
				nameOfOrganisation,
				country,
				formType,
				nameOfAssociation,
				association,
				countryAssociation,
			},
		});
	}, [
		email,
		title,
		firstName,
		lastName,
		nameOfOrganisation,
		country,
		formType,
		nameOfAssociation,
		association,
		countryAssociation,
		currentPage,
		rowLimit,
		loadDetail,
	]);

	const handleRowClick = (guestId) => {
		loadDetailById({ variables: { id: guestId } });
		setSelectedGuest(guestId);
		onOpen();
	};

	const handleScan = async (data) => {
		if (data) {
			const id = data.text || data;
			setScannedGuestId(id);

			try {
				loadDetailById({ variables: { id } });
				onCloseQr();
				onOpenConfirm();
			} catch (error) {
				console.error('Failed to load guest details from QR code:', error);
			}
		}
	};

	const handleConfirmCheckIn = async () => {
		try {
			await loadCheckInGuest({ variables: { id: scannedGuestId } });
			loadDetailById({ variables: { id: scannedGuestId } });
			setSelectedGuest(scannedGuestId);
			setQrResult(scannedGuestId);
			onCloseConfirm();
			onCloseQr();
			onOpen();
		} catch (error) {
			console.error('Check-in failed', error);
		}
	};

	const handleConfirmCheckOut = async () => {
		try {
			const id = scannedGuestId || selectedGuest || detail?.id;
			if (!id) {
				console.error('No guest ID found for check out');
				return;
			}

			const { data } = await loadCheckOutGuest({ variables: { id } });
			if (data?.checkOutGuest) {
				setDetail((prev) => ({
					...prev,
					checkInStatus: false,
					checkInAt: null,
					checkOutStatus: data?.checkOutGuest?.checkOutStatus,
					checkOutAt: data?.checkOutGuest?.checkOutAt,
				}));
			}
			await loadDetailById({ variables: { id } });
			setSelectedGuest(id);
			onCloseConfirm();
			onCloseQr();
			onOpen();
		} catch (error) {
			console.error('Check-out failed', error);
		}
	};

	const all = data?.getAllAIRMRegistrations?.airmForm ?? [];
	const regulators = all.filter((option) => option.formType !== 'association');

	const [associations, setAssociations] = useState([]);
	useEffect(() => {
		if (all) {
			setAssociations(all.filter((option) => option.formType === 'association'));
		}
	}, [all]);

	const sortedAssociations = [...associations].sort((a, b) => {
		const tA = a.checkInAssociation ? new Date(a.checkInAssociation).getTime() : 0;
		const tB = b.checkInAssociation ? new Date(b.checkInAssociation).getTime() : 0;

		return tB - tA;
	});

	const handleConfirmCheckOutAssociation = async (id) => {
		try {
			const { data: checkedOutData } = await loadCheckOutAssociation({ variables: { id } });

			if (associations) {
				const tempAssociations = associations?.map((item) =>
					item?.id === checkedOutData?.checkOutAssociation?.id
						? { ...item, checkOutAssociation: checkedOutData?.checkOutAssociation?.checkOutAssociation }
						: { ...item }
				);
				setAssociations(tempAssociations);
			}
			await loadDetailById({ variables: { id } });
		} catch (error) {
			console.error('Check-out failed', error);
		}
	};

	const sortedRegulators =
		sortCountryAsc === null
			? regulators
			: [...regulators]?.sort((a, b) => {
					const countryA = a.country || '';
					const countryB = b.country || '';
					if (countryA === countryB) return 0;

					if (sortCountryAsc) {
						// ascending
						return countryA > countryB ? 1 : -1;
					}
					// descending
					return countryA < countryB ? 1 : -1;
			  });

	// if (sortCheckInAsc !== null) {
	// 	displayedData = [...displayedData].sort((a, b) => {
	// 		if (a.checkInStatus === b.checkInStatus) return 0;
	// 		if (sortCheckInAsc) {
	// 			return a.checkInStatus ? 1 : -1;
	// 		}
	// 		return a.checkInStatus ? -1 : 1;
	// 	});
	// }

	const handleError = (err) => {
		console.error('QR Scan Error:', err);
	};

	return (
		<>
			<Text textAlign="center" fontWeight="bold" fontSize={30} mt={'20px'}>
				AIRM Guest List
			</Text>
			<Container maxW="container.xl" mt="20px" backgroundColor={'white'}>
				<Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
					<TabList borderBottomStyle="solid" borderBottomColor="gray.200">
						<Tab>Regulator</Tab>
						<Tab>Other Institutions</Tab>
					</TabList>
					<Flex justifyContent={{ md: 'flex-end', sm: 'center', base: 'center' }} mt={4}>
						{tabIndex === 0 && (
							<Flex justifyContent={{ md: 'flex-end', sm: 'center', base: 'center' }} mt={4} mr={8}>
								<Flex>
									<Button
										onClick={() => {
											loadExportData();
										}}
										width="90px"
										colorScheme="blue"
										mr={2}
									>
										Export CSV
									</Button>
									<Button onClick={onOpenQr} fontSize="30px" size="md" width="80px" colorScheme="blue">
										<FontAwesomeIcon icon={faQrcode} style={{ fontSize: 25, color: 'white' }} />
									</Button>
								</Flex>
							</Flex>
						)}
						{tabIndex === 1 && (
							<Flex justifyContent={{ md: 'flex-end', sm: 'center', base: 'center' }} mt={4} mr={7}>
								<Flex>
									<Button
										onClick={() => {
											loadAssociationExportData();
										}}
										width="90px"
										colorScheme="blue"
										mr={2}
									>
										Export CSV
									</Button>
								</Flex>
							</Flex>
						)}
					</Flex>
					<TabPanels>
						<TabPanel>
							<Container maxW="container.xl">
								<Center mt="2">
									<Box
										w="100%"
										bg="gray.100"
										borderRadius={c.borderRadius}
										border="1px solid #dbdbdb"
										boxShadow="0 1px 2px rgba(var(--black, #00rgba(179, 13, 13, 1)0.1)"
										pb="32px"
									>
										<Box>
											<Table variant="simple" className="responsive-table-container">
												<Thead>
													<Tr>
														<Th>No.</Th>
														<Th>Title</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>First Name</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Last Name</Th>
														<Th>Email</Th>
														<Th>Name of Organisation</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>
															Country
															{showLargeScreen && (
																<Button
																	size="xs"
																	ml={2}
																	variant="ghost"
																	onClick={() => setSortCountryAsc((prev) => (prev === null ? true : !prev))}
																>
																	{sortCountryAsc === null ? '⇅' : sortCountryAsc ? '▲' : '▼'}
																</Button>
															)}
														</Th>
														<Th>Check-In</Th>
														<Th>Check-Out</Th>
													</Tr>
												</Thead>
												<Tbody>
													<>
														{regulators?.length <= 0 && (
															<Tr>
																<Td>
																	<Text>{t('No Data')}</Text>
																</Td>
															</Tr>
														)}
														{sortedRegulators?.map((item, index) => {
															return (
																<Tr key={item?.id} onClick={() => handleRowClick(item?.id)} style={{ cursor: 'pointer' }}>
																	<Td align="center">{index + 1 < 10 ? `0${index + 1}` : index + 1}</Td>
																	<Td align="center">{item?.title}</Td>
																	<Td align="center">{item?.firstName}</Td>
																	<Td align="center">{item?.lastName}</Td>
																	<Td align="center">{item?.email}</Td>
																	<Td align="center">{item?.nameOfOrganisation}</Td>
																	<Td align="center">{item?.country}</Td>
																	{/* <Td align="center">
                                                            {item?.checkInStatus === true ? (
                                                                <Badge backgroundColor="green.500" alignItems="center" justifyContent="center" py={1} px={2}>
                                                                    <Text fontSize="12px" color="white">
                                                                        ✓
                                                                    </Text>
                                                                </Badge>
                                                            ) : (
                                                                <Badge backgroundColor="red.500" alignItems="center" justifyContent="center" py={1} px={2}>
                                                                    <Text fontSize="12px" color="white">
                                                                        ---
                                                                    </Text>
                                                                </Badge>
                                                            )}
                                                        </Td> */}
																	<Td align="center" style={{ whiteSpace: 'nowrap' }}>
																		{timeFormat(item?.checkInAt)}
																	</Td>
																	<Td align="center" style={{ whiteSpace: 'nowrap' }}>
																		{timeFormat(item?.checkOutAt)}
																	</Td>
																</Tr>
															);
														})}
													</>
												</Tbody>
											</Table>
										</Box>
									</Box>
								</Center>
							</Container>
						</TabPanel>
						<TabPanel>
							<Container maxW="container.xl">
								<Center mt="2">
									<Box
										w="100%"
										bg="gray.100"
										borderRadius={c.borderRadius}
										border="1px solid #dbdbdb"
										boxShadow="0 1px 2px rgba(var(--black, #00rgba(179, 13, 13, 1)0.1)"
										pb="32px"
									>
										<Box>
											<Table variant="simple" className="responsive-table-container">
												<Thead>
													<Tr>
														<Th>No.</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Full Name</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Country</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Name of Institution</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Check-In</Th>
														<Th style={{ whiteSpace: 'nowrap' }}>Check-Out</Th>
													</Tr>
												</Thead>
												<Tbody>
													<>
														{sortedAssociations?.length <= 0 && (
															<Tr>
																<Td>
																	<Text>{t('No Data')}</Text>
																</Td>
															</Tr>
														)}
														{sortedAssociations?.map((item, index) => {
															return (
																<Tr key={item?.id} style={{ cursor: 'pointer' }}>
																	<Td align="center">{index + 1 < 10 ? `0${index + 1}` : index + 1}</Td>
																	<Td align="center">{item?.nameOfAssociation}</Td>
																	<Td align="center">{item?.countryAssociation}</Td>
																	<Td align="center">{item?.association}</Td>
																	<Td align="center" style={{ whiteSpace: 'nowrap' }}>
																		{timeFormat(item?.checkInAssociation)}
																	</Td>
																	{item?.checkOutAssociation ? (
																		<Td align="center" style={{ whiteSpace: 'nowrap' }}>
																			{timeFormat(item?.checkOutAssociation)}
																		</Td>
																	) : (
																		<Flex mt={2} justify="center">
																			<Button
																				onClick={() => handleConfirmCheckOutAssociation(item?.id)}
																				colorScheme="red"
																				isLoading={loadingCheckOutAssociation}
																			>
																				Check-out
																			</Button>
																		</Flex>
																	)}
																</Tr>
															);
														})}
													</>
												</Tbody>
											</Table>
										</Box>
									</Box>
								</Center>
							</Container>
						</TabPanel>
					</TabPanels>
				</Tabs>

				<Modal isOpen={isOpen} onClose={onClose} size="4xl">
					<ModalOverlay />
					<ModalContent borderRadius={c.borderRadius}>
						<ModalHeader>{t('Guest Details')}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							{loadingDetailById ? (
								<Text>{t('Loading...')}</Text>
							) : (
								<>
									<Flex justify="center" align="end">
										{dataDetailById?.getAIRMRegistrationById?.idPhoto && (
											<>
												<Image
													userSelect="none"
													src={dataDetailById?.getAIRMRegistrationById?.idPhoto}
													width="300px"
													height="auto"
													alt="ID photo"
												/>
											</>
										)}
									</Flex>

									<Flex justifyContent="center" mt={2}>
										<SmallDownloadAndViewButton url={dataDetailById?.getAIRMRegistrationById?.idPhoto} />
									</Flex>
									<Stack mt={4} pb={10}>
										<SimpleGrid columns={{ md: 2, sm: 1 }}>
											<Box gridColumn={showLargeScreen ? 1 : 0} spacing="50px">
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Email')}:</strong> {dataDetailById?.getAIRMRegistrationById?.email}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Title')}:</strong> {dataDetailById?.getAIRMRegistrationById?.title}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('First Name')}:</strong> {dataDetailById?.getAIRMRegistrationById?.firstName}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Last Name')}:</strong> {dataDetailById?.getAIRMRegistrationById?.lastName}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Phone Number / WhatsApp')}:</strong> {dataDetailById?.getAIRMRegistrationById?.phoneNumber}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Name of Organisation')}:</strong> {dataDetailById?.getAIRMRegistrationById?.nameOfOrganisation}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Designation')}:</strong> {dataDetailById?.getAIRMRegistrationById?.designation}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Country')}:</strong> {dataDetailById?.getAIRMRegistrationById?.country}
												</Text>
												<Flex gap={2} mt={1}>
													<Text as="span" fontWeight="bold">
														{t('Status of Participation')}:
													</Text>

													<Text
														bg={
															dataDetailById?.getAIRMRegistrationById?.statusOfParticipation === 'Head of Delegate'
																? 'yellow.300'
																: 'transparent'
														}
														px={1}
														borderRadius="md"
													>
														{dataDetailById?.getAIRMRegistrationById?.statusOfParticipation}
													</Text>
												</Flex>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<Flex>
														<Text alignContent="center">
															<strong>{t('Attached Passport Document')}:</strong>
														</Text>
														<Box ml="-4">
															<DownloadAndViewButton
																marginLeft="50px"
																url={dataDetailById?.getAIRMRegistrationById?.passportAttachment}
															/>
														</Box>
													</Flex>
												</Text>
												{dataDetailById?.getAIRMRegistrationById?.event?.length ? (
													<>
														<Text fontWeight="bold">{t('Event Attendance')}:</Text>

														{dataDetailById?.getAIRMRegistrationById?.event?.map((event, idx) => (
															<ListItem ml={4} key={idx} width="370px">
																{event}
															</ListItem>
														))}
													</>
												) : (
													<Text _hover={{ bg: 'gray.100' }} mt={1}>
														<strong>{t('Event Attendance')}:</strong> {t('None')}
													</Text>
												)}
												{dataDetailById?.getAIRMRegistrationById?.dinnerEvent?.length ? (
													<>
														<Text fontWeight="bold">{t('Dinner Attendance')}:</Text>
														{dataDetailById?.getAIRMRegistrationById?.dinnerEvent?.map((event, idx) => (
															<ListItem ml={4} key={idx}>
																{event}
															</ListItem>
														))}
													</>
												) : (
													<Text _hover={{ bg: 'gray.100' }} mt={1}>
														<strong>{t('Dinner Attendance')}:</strong> {t('None')}
													</Text>
												)}
											</Box>

											<Box gridColumn={showLargeScreen ? 2 : 0}>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Dietary')}:</strong> {detail?.dietary ? detail?.dietary : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Flight No')}:</strong> {detail?.flightNo ? detail?.flightNo : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Airline')}:</strong> {detail?.airline ? detail?.airline : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Airport')}:</strong> {detail?.airport ? detail?.airport : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Date of Arrival / Time of Arrival')}:</strong>{' '}
													{detail?.dateTimeOfArrival ? timeFormat(detail?.dateTimeOfArrival) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Date of Departure / Time of Departure')}:</strong>{' '}
													{detail?.dateTimeOfDeparture ? timeFormat(detail?.dateTimeOfDeparture) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel')}:</strong> {detail?.hotel ? detail?.hotel : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel Check In')}:</strong> {detail?.checkIn ? timeFormat(detail?.checkIn) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel Check Out')}:</strong>{' '}
													{detail?.checkOut ? timeFormat(detail?.checkOut) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Name of Accompanying Person')}:</strong>{' '}
													{detail?.nameOfAccompanyPerson ? detail?.nameOfAccompanyPerson : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Relationship')}:</strong> {detail?.relationship ? detail?.relationship : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Registered On')}:</strong> {timeFormat(dataDetailById?.getAIRMRegistrationById?.createdAt)}
												</Text>
												{dataCheckInGuest?.checkInGuest?.checkInStatus ||
												dataDetailById?.getAIRMRegistrationById?.checkInStatus === true ? (
													<>
														<Flex mt={3}>
															{dataDetailById?.getAIRMRegistrationById?.checkOutStatus === true ? (
																<>
																	<Flex flexDirection="column">
																		<Badge backgroundColor="green.500">
																			<Text fontSize="15px" color="white">
																				Checked In: {timeFormat(dataDetailById?.getAIRMRegistrationById?.checkInAt)}
																			</Text>
																		</Badge>
																		<Box mt={2}>
																			<Badge backgroundColor="red.500">
																				<Text fontSize="15px" color="white">
																					Checked Out:
																					{timeFormat(dataDetailById?.getAIRMRegistrationById?.checkOutAt)}
																				</Text>
																			</Badge>
																		</Box>
																	</Flex>
																</>
															) : dataDetailById?.getAIRMRegistrationById?.checkInStatus === true ? (
																<Badge backgroundColor="green.500">
																	<Text fontSize="15px" color="white">
																		Checked In: {timeFormat(dataDetailById?.getAIRMRegistrationById?.checkInAt)}
																	</Text>
																</Badge>
															) : (
																<Badge backgroundColor="gray.400">
																	<Text fontSize="15px" color="white">
																		---
																	</Text>
																</Badge>
															)}
														</Flex>
														{dataDetailById?.getAIRMRegistrationById?.checkInStatus === true &&
														!dataDetailById?.getAIRMRegistrationById?.checkOutStatus ? (
															<Flex mt={5} justify="flex-start">
																<Button
																	onClick={() => {
																		handleConfirmCheckOut();
																		window.location.reload();
																	}}
																	colorScheme="red"
																>
																	Check-out
																</Button>
															</Flex>
														) : null}
													</>
												) : (
													<>
														<Flex mt={3}>
															<Badge backgroundColor="red.500">
																<Text fontSize="15px" color="white">
																	---
																</Text>
															</Badge>
														</Flex>
													</>
												)}
											</Box>
										</SimpleGrid>
									</Stack>
								</>
							)}
						</ModalBody>
					</ModalContent>
				</Modal>

				<Modal isOpen={isOpenQr} onClose={onCloseQr} size="2xl">
					<ModalOverlay />
					<ModalContent borderRadius={c.borderRadius}>
						<ModalHeader>{t('Guest Check-in (QR Scanner)')}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							{loadingDetailById ? (
								<Text>{t('Loading...')}</Text>
							) : (
								<>
									<Box justifyContent="center" alignItems="center" p={4}>
										<QrScannerWithFlip onScan={handleScan} onError={handleError} />
									</Box>
								</>
							)}
						</ModalBody>
					</ModalContent>
				</Modal>

				<Modal isOpen={isOpenConfirm} onClose={onCloseConfirm} size="4xl">
					<ModalOverlay />
					<ModalContent borderRadius={c.borderRadius}>
						<ModalHeader>{t('Guest Details')}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							{loadingDetailById ? (
								<Text>{t('Loading...')}</Text>
							) : (
								<>
									<Flex justify="center" align="end">
										{dataDetailById?.getAIRMRegistrationById?.idPhoto && (
											<>
												<Image
													userSelect="none"
													src={dataDetailById?.getAIRMRegistrationById?.idPhoto}
													width="300px"
													height="auto"
													alt="ID photo"
												/>
											</>
										)}
									</Flex>

									<Flex justifyContent="center" mt={2}>
										<SmallDownloadAndViewButton url={dataDetailById?.getAIRMRegistrationById?.idPhoto} />
									</Flex>
									<Stack mt={4} pb={10}>
										<SimpleGrid columns={{ md: 2, sm: 1 }}>
											<Box gridColumn={showLargeScreen ? 1 : 0} spacing="50px">
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Email')}:</strong> {dataDetailById?.getAIRMRegistrationById?.email}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Title')}:</strong> {dataDetailById?.getAIRMRegistrationById?.title}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('First Name')}:</strong> {dataDetailById?.getAIRMRegistrationById?.firstName}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Last Name')}:</strong> {dataDetailById?.getAIRMRegistrationById?.lastName}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Phone Number / WhatsApp')}:</strong> {dataDetailById?.getAIRMRegistrationById?.phoneNumber}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Name of Organisation')}:</strong> {dataDetailById?.getAIRMRegistrationById?.nameOfOrganisation}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Designation')}:</strong> {dataDetailById?.getAIRMRegistrationById?.designation}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Country')}:</strong> {dataDetailById?.getAIRMRegistrationById?.country}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Status of Participation')}:</strong>{' '}
													{dataDetailById?.getAIRMRegistrationById?.statusOfParticipation}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<Flex>
														<Text alignContent="center">
															<strong>{t('Attached Passport Document')}:</strong>
														</Text>
														<Box ml="-4">
															<DownloadAndViewButton
																marginLeft="50px"
																url={dataDetailById?.getAIRMRegistrationById?.passportAttachment}
															/>
														</Box>
													</Flex>
												</Text>
												{dataDetailById?.getAIRMRegistrationById?.event?.length ? (
													<>
														<Text fontWeight="bold">{t('Event Attendance')}:</Text>
														{dataDetailById?.getAIRMRegistrationById?.event?.map((event, idx) => (
															<ListItem ml={4} key={idx} width="370px">
																{event}
															</ListItem>
														))}
													</>
												) : (
													<Text _hover={{ bg: 'gray.100' }} mt={1}>
														<strong>{t('Event Attendance')}:</strong> {t('None')}
													</Text>
												)}
												{dataDetailById?.getAIRMRegistrationById?.dinnerEvent?.length ? (
													<>
														<Text fontWeight="bold">{t('Dinner Attendance')}:</Text>
														{dataDetailById?.getAIRMRegistrationById?.dinnerEvent?.map((event, idx) => (
															<ListItem ml={4} key={idx}>
																{event}
															</ListItem>
														))}
													</>
												) : (
													<Text _hover={{ bg: 'gray.100' }} mt={1}>
														<strong>{t('Dinner Attendance')}:</strong> {t('None')}
													</Text>
												)}
											</Box>

											<Box gridColumn={showLargeScreen ? 2 : 0}>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Dietary')}:</strong> {detail?.dietary ? detail?.dietary : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Flight No')}:</strong> {detail?.flightNo ? detail?.flightNo : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Airline')}:</strong> {detail?.airline ? detail?.airline : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Airport')}:</strong> {detail?.airport ? detail?.airport : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Date of Arrival / Time of Arrival')}:</strong>{' '}
													{detail?.dateTimeOfArrival ? timeFormat(detail?.dateTimeOfArrival) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Date of Departure / Time of Departure')}:</strong>{' '}
													{detail?.dateTimeOfDeparture ? timeFormat(detail?.dateTimeOfDeparture) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel')}:</strong> {detail?.hotel ? detail?.hotel : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel Check In')}:</strong> {detail?.checkIn ? timeFormat(detail?.checkIn) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Hotel Check Out')}:</strong>{' '}
													{detail?.checkOut ? timeFormat(detail?.checkOut) : 'To be confirmed'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Name of Accompanying Person')}:</strong>{' '}
													{detail?.nameOfAccompanyPerson ? detail?.nameOfAccompanyPerson : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Relationship')}:</strong> {detail?.relationship ? detail?.relationship : 'N/A'}
												</Text>
												<Text _hover={{ bg: 'gray.100' }} mt={1}>
													<strong>{t('Registered On')}:</strong> {timeFormat(detail?.createdAt)}
												</Text>
												{dataCheckInGuest?.checkInGuest?.checkInStatus ||
												dataDetailById?.getAIRMRegistrationById?.checkInStatus === true ? (
													<>
														<Flex mt={3}>
															{dataDetailById?.getAIRMRegistrationById?.checkOutStatus === true ? (
																<Flex flexDirection="column">
																	<Badge backgroundColor="green.500">
																		<Text fontSize="15px" color="white">
																			Checked In: {timeFormat(dataDetailById?.getAIRMRegistrationById?.checkInAt)}
																		</Text>
																	</Badge>
																	<Box mt={2}>
																		<Badge backgroundColor="red.500">
																			<Text fontSize="15px" color="white">
																				Checked Out: {timeFormat(dataDetailById?.getAIRMRegistrationById?.checkOutAt)}
																			</Text>
																		</Badge>
																	</Box>
																</Flex>
															) : dataDetailById?.getAIRMRegistrationById?.checkInStatus === true ? (
																<Badge backgroundColor="green.500">
																	<Text fontSize="15px" color="white">
																		Checked In: {timeFormat(dataDetailById?.getAIRMRegistrationById?.checkInAt)}
																	</Text>
																</Badge>
															) : (
																<Badge backgroundColor="gray.400">
																	<Text fontSize="15px" color="white">
																		---
																	</Text>
																</Badge>
															)}
														</Flex>
														{dataDetailById?.getAIRMRegistrationById?.checkInStatus === true &&
														!dataDetailById?.getAIRMRegistrationById?.checkOutStatus ? (
															<Flex mt={5} justify="flex-start">
																<Button
																	onClick={() => {
																		handleConfirmCheckOut();
																		window.location.reload();
																	}}
																	colorScheme="red"
																>
																	Check-out
																</Button>
															</Flex>
														) : null}
													</>
												) : (
													<>
														<Flex mt={3}>
															<Badge backgroundColor="red.500">
																<Text fontSize="15px" color="white">
																	---
																</Text>
															</Badge>
														</Flex>
													</>
												)}
												{dataDetailById?.getAIRMRegistrationById?.checkInStatus === null && (
													<Flex justifyContent={{ base: 'center', md: 'flex-end' }} gap={4} mt={4}>
														<Button onClick={onCloseConfirm}>{t('Cancel')}</Button>
														<Button
															onClick={() => {
																handleConfirmCheckIn();
																window.location.reload();
															}}
															colorScheme="blue"
															ml={4}
														>
															Confirm Check-in
														</Button>
													</Flex>
												)}
											</Box>
										</SimpleGrid>
									</Stack>
								</>
							)}
						</ModalBody>
					</ModalContent>
				</Modal>
			</Container>
		</>
	);
}

export default AIRMReport;
